class InfluenceElement extends HTMLElement {
  constructor() {
    super();
    
    this.initData = this.dataset;
    window.influence_data = window.influence_data || {};

    this.settings = null;
    if (this.hasAttribute('data-settings')) {
      this.settings = JSON.parse(this.getAttribute('data-settings'));
    }

    this.reInitDelay = 5000;
  }
  
  connectedCallback() {
    this.init();
  }

  /**
   * Refreshes the data and the content of the component
   */
  refresh() {
    setTimeout(() => {
      this.init(true);
    }, this.reInitDelay);
  }
  
  /**
   * Translates a text based on the localization metaobject, which was passed to the component via data-settings.
   * @param {string} original 
   * @returns {string}
   */
  _t(original) {
    if (!this.settings || !this.settings.localization) return original;
    if (!this.settings.localization[original.trim()]) return original;
    const field = this.settings.localization[original.trim()];

    if (field.trim() === "") return original;
    return field;
  }

  /**
   * Fetches an URL until it gives a successful response, and caches it.
   * @param {string} url The URL to be requested
   * @param {object} options custom options for the fetch method. By default, the Content-Type is set to application/json in the method
   * @param {boolean} invalidateCache indicates whether the cache should be invalidated, and the data should be requested again
   * @returns {object} the response object
   */
  _fetchWithRetry(url, options = {}, invalidateCache = false) {
    return new Promise(async (resolve, _) => {
      // Invalidate cache if needed
      if (invalidateCache) {
        delete window.influence_data[url];
      }
      
      // Return cached value if present
      if (this._inCache(url)) {
        resolve(window.influence_data[url]);
        return;
      }

      // Add to callback list if the current URL is already being fetched
      const requestingKey = `${url}_requesting`;
      if (typeof window.influence_data[requestingKey] !== 'undefined') {
        this._addCallback(url, resolve);
        return;
      }

      // Set requesting flag to true to indicate for other components that this URL is already being fetched
      window.influence_data[requestingKey] = true;

      if (typeof options.headers === "undefined") {
        options.headers = {
          'Content-Type': 'application/json'
        }
      }

      while (true) {
        try {
          const response = await fetch(url, options);

          if (response.ok) {
            window.influence_data[url] = await response.json();

            // Invoke all callbacks that are waiting for this fetch to complete
            this._invokeCallbacks(url);

            // Delete requesting flag, so if a refetch is happening, 
            delete window.influence_data[requestingKey];

            resolve(window.influence_data[url]);
            return;
          }
        } catch (error) {
          console.warn("Fetch error, retrying...", error);
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    });
  }

  /**
   * Checks if a given URL has already been requested and cached.
   * @param {string} url the URL to check the request of
   * @returns 
   */
  _inCache(url) {
    return typeof window.influence_data[url] !== 'undefined';
  }

  /**
   * Attaches a callback to a URL, which gets called when the request is completed.
   * @param {string} url The request's URL to attach the callback to
   * @param {Function} callback the callback itself that needs to be executed when the request of the provided URL is completed
   */
  _addCallback(url, callback) {
    const callbackKey = `${url}_callbacks`;
    
    window.influence_data[callbackKey] = window.influence_data[callbackKey] || [];
    window.influence_data[callbackKey].push(callback.bind(this));
  }

  /**
   * Invokes all of the callbacks of a given URL, and deletes the callback array
   * @param {string} url the URL of the request which's callbacks need to be called
   */
  _invokeCallbacks(url) {
    const callbackKey = `${url}_callbacks`;

    // If no method was added as a callback, return
    if (typeof window.influence_data[callbackKey] === 'undefined') {
      return;
    }

    // Go through all of the callbacks and execute them
    for (const callback of window.influence_data[callbackKey]) {
      callback(window.influence_data[url]);
    }

    // delete the callback array
    delete window.influence_data[callbackKey];
  }

  // Adding init function in case the child class does not implement it
  async init(_) {}
}
/*
 * ------------------------------
 * EARN POINTS
 * ------------------------------
*/
class InfluenceEarnPoints extends InfluenceElement {
  constructor() {
    super();

    this.customer = null;
    this.earn = null;
    this.history = null;
  }
  
  async init(refresh = false) {
    if (typeof this.initData.customerId !== 'undefined' && this.initData.customerId !== null) {
      this.customer = await this._fetchWithRetry(
        `https://launcher.api.influence.io/launcher/v1/customer?${
          new URLSearchParams({
            customer_id: this.initData.customerId,
            customer_email: this.initData.customerEmail,
            shop: this.initData.shop,
            digest: this.initData.customerDigest,
          })
        }`, {}, refresh
      );

      this.history = await this._fetchWithRetry(
        `https://launcher.api.influence.io/launcher/v1/customer/points?${
          new URLSearchParams({
            customer_id: this.initData.customerId,
            customer_email: this.initData.customerEmail,
            shop: this.initData.shop,
            digest: this.initData.customerDigest,
            limit: 99
          })
        }`, {}, refresh
      );
    }

    this.earn = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/shop/rules/earn?${
        new URLSearchParams({
          shop: this.initData.shop,
        })
      }`, {}, refresh
    );

    this._generateEarn();

    MicroModal.init({
      openTrigger: 'data-custom-open',
      closeTrigger: 'data-custom-close',
      openClass: 'is-open',
      disableScroll: true,
      disableFocus: false,
      awaitOpenAnimation: true,
      awaitCloseAnimation: true,
      debugMode: false
    });

    if (!refresh) {
      const modalButtons = this.querySelectorAll('[data-custom-close="influence_birthday_modal"]');
      modalButtons.forEach(button => {
        button.addEventListener('click', () => {
          MicroModal.close('influence_birthday_modal');
        })
      });
      const birthdayModalSubmit = this.querySelector('#birthday-modal-submit');
      const birthdayYearModalinput = this.querySelector('#birthday-year');
  
      birthdayModalSubmit.addEventListener('click', this._submitBirthday.bind(this));
      birthdayYearModalinput.addEventListener('keyup', (e) => {
        if (e.target.value && birthdayModalSubmit && birthdayModalSubmit.hasAttribute('disabled')) {
          birthdayModalSubmit.removeAttribute('disabled');
        }
  
        if (!e.target.value && birthdayModalSubmit && !birthdayModalSubmit.hasAttribute('disabled')) {
          birthdayModalSubmit.setAttribute('disabled', true);
        }
      })
    }
  }

  _updateTable() {
    const updateSelectors = [ 'influence-rewards', 'influence-history', 'influence-self-points' ];
    for (const selector of updateSelectors) {
      const elem = document.querySelector(selector);
      if (elem) {
        elem.refresh();
      }
    }
  }

  _generateEarn() {
    if (this.earn) {
      const earnRules = this.earn?.rules;
      const earnElement = this.querySelector('.influence-user-data-earn-items');

      if (earnElement && earnRules && Array.isArray(earnRules)) {
        earnElement.innerHTML = '';
        const filteredEarnRules = earnRules.filter(rule => {
          const loyaltyTierIds = [...rule?.loyaltyTierIds];

          if (rule?.expiryDate) {
            const currentDateTime = new Date();
            const expiryDate = new Date(rule?.expiryDate);
            return rule?.status == "Active" && (loyaltyTierIds.length == 0 || loyaltyTierIds?.includes(this.customer?.currentLoyaltyTierId)) && currentDateTime < expiryDate;
          } else {
            return rule?.status == "Active" && (loyaltyTierIds.length == 0 || loyaltyTierIds?.includes(this.customer?.currentLoyaltyTierId));
          }
        });

        const earnIcons = this.settings.earn_icons;

        filteredEarnRules.forEach(rule => {
          const newDiv = document.createElement('div');
          newDiv.classList.add('influence-user-data-earn-item');

          const iconElement = document.createElement('div');
          iconElement.classList.add('influence-user-data-earn-item-icon');
          iconElement.innerHTML = earnIcons[rule?.templateName];

          const titleElement = document.createElement('div');
          titleElement.classList.add('influence-user-data-earn-item-title');
          titleElement.innerHTML = `${this._t(rule.customTitle)}`;

          const earnValueElement = document.createElement('div');
          earnValueElement.classList.add('influence-user-data-earn-item-cost');

          const earnButtonWrapperElement = document.createElement('div');
          earnButtonWrapperElement.classList.add('influence-user-data-earn-button-wrapper');

          const isAlreadyEarned = this.history?.items?.find(hi => {
            return hi.earnRuleId == rule.id;
          });

          switch (rule?.templateName) {
            case "placeorder":
              earnValueElement.innerHTML = this.settings.x_points_per_order.replace('%points%', rule.earnValue);
              break;
            default:
              earnValueElement.innerHTML = this.settings.x_points.replace('%points%', rule.earnValue);
          }

          if (this.customer) {
            switch (rule?.templateName) {
              case "followinstagram":
                const followinstagramButton = document.createElement('button');
                followinstagramButton.innerText = this.customer?.followInstagram ? this.settings.already_earned : this.settings.follow_instagram;
                followinstagramButton.className = 'button influence-user-data-earn-button';

                if (this.customer?.followInstagram) {
                  followinstagramButton.setAttribute('disabled', 'true');
                }

                followinstagramButton.addEventListener('click', (ev) => this._submitInstagram(ev, rule?.id, rule?.socialUrl))

                earnButtonWrapperElement.appendChild(followinstagramButton);
                break;
              case "followtiktok":
                const followtiktokButton = document.createElement('button');
                followtiktokButton.innerText = this.customer?.followTikTok ? this.settings.already_earned : this.settings.follow_tiktok;
                followtiktokButton.className = 'button influence-user-data-earn-button';

                if (this.customer?.followTikTok) {
                  followtiktokButton.setAttribute('disabled', 'true');
                }

                followtiktokButton.addEventListener('click', (ev) => this._submitTikTok(ev, rule?.id, rule?.socialUrl))

                earnButtonWrapperElement.appendChild(followtiktokButton);
                break;
              case "birthday":
                const birthdayButton = document.createElement('button');
                birthdayButton.setAttribute('id', 'earn-birthday-button')
                birthdayButton.innerText = this.customer?.birthdayDay && this.customer?.birthdayMonth && this.customer?.birthdayYear ? this.settings.already_earned : this.settings.set_birthday;
                birthdayButton.className = 'button influence-user-data-earn-button';

                if (this.customer?.birthdayDay && this.customer?.birthdayMonth && this.customer?.birthdayYear ) {
                  birthdayButton.setAttribute('disabled', 'true');
                }

                birthdayButton.addEventListener('click', this._birthdayModalOpen);

                earnButtonWrapperElement.appendChild(birthdayButton);
                break;
              default:
                earnButtonWrapperElement.classList.add('hidden');
            }
          } else {
            earnButtonWrapperElement.classList.add('hidden');
          }
          

          newDiv.appendChild(iconElement);
          newDiv.appendChild(titleElement);
          newDiv.appendChild(earnValueElement);
          newDiv.appendChild(earnButtonWrapperElement);

          earnElement.appendChild(newDiv);
        })
      }
    }
  }

  _submitTikTok(ev, ruleId, tag) {
    window.open(`https://www.tiktok.com/@${tag}`, '_blank', 'toolbar=0,location=0,menubar=0');

    const { target: button } =  ev;
    button.classList.add('loading-button');

    const options = {
      method: 'POST',
      headers: {accept: 'application/json', 'content-type': 'application/json'},
      body: JSON.stringify({
        customer: {
          id: this.initData?.customerId,
          email: this.initData?.customerEmail
        },
        ruleId: ruleId,
        shop: this.initData?.shop,
        digest: this.initData?.customerDigest,
      })
    };

    fetch('https://launcher.api.influence.io/launcher/v1/customer/social', options)
      .then(res => res.json())
      .then(res => {
        if (res?.success) {
          button.innerText = this.settings.already_earned;
          button.setAttribute('disabled', true);
        }
        this._updateTable();
        button.classList.remove('loading-button');
      })
      .catch(err => {
        console.error(err);
        button.classList.remove('loading-button');
      });
  }

  _submitInstagram(ev, ruleId, tag) {
    window.open(`https://www.instagram.com/${tag}`, '_blank', 'toolbar=0,location=0,menubar=0');

    const { target: button } = ev;
    button.classList.add('loading-button');

    const options = {
      method: 'POST',
      headers: {accept: 'application/json', 'content-type': 'application/json'},
      body: JSON.stringify({
        customer: {
          id: this.initData?.customerId,
          email: this.initData?.customerEmail
        },
        ruleId: ruleId,
        shop: this.initData?.shop,
        digest: this.initData?.customerDigest,
      })
    };

    fetch('https://launcher.api.influence.io/launcher/v1/customer/social', options)
      .then(res => res.json())
      .then(res => {
        if (res?.success) {
          button.innerText = this.settings.already_earned;
          button.setAttribute('disabled', true);
        }
        this._updateTable();
        button.classList.remove('loading-button');
      })
      .catch(err => {
        console.error(err);
        button.classList.remove('loading-button');
      });
  }

  _submitBirthday(event) {
    const { target: button } = event;
    const day = event.target.parentElement.querySelector('#birthday-day').value;
    const month = event.target.parentElement.querySelector('#birthday-month').value;
    const year = event.target.parentElement.querySelector('#birthday-year').value;

    const options = {
      method: 'PUT',
      headers: {accept: 'application/json', 'content-type': 'application/json'},
      body: JSON.stringify({
        customer: {
          id: this.initData?.customerId,
          email: this.initData?.customerEmail
        },
        shop: this.initData?.shop,
        digest: this.initData?.customerDigest,
        birthdayDay: Number(day),
        birthdayMonth: Number(month),
        birthdayYear: Number(year),
      })
    };

    button.classList.add('loading-button');

    fetch('https://launcher.api.influence.io/launcher/v1/customer/birthday', options)
      .then(res => res.json())
      .then(res => {
        MicroModal.close('influence_birthday_modal');
        button.classList.remove('loading-button');

        this._updateTable();
        
        const earnBirthdayButton = this.querySelector('#earn-birthday-button');

        earnBirthdayButton.setAttribute('disabled', true);
        earnBirthdayButton.innerText = this.settings.already_earned;
      })
      .catch(err => {
        console.error(err);
        button.classList.remove('loading-button');
      });

  }

  _birthdayModalOpen() {
    MicroModal.show('influence_birthday_modal');
  }
}

if (typeof window.customElements.get('influence-earn-points') === 'undefined') {
  window.customElements.define('influence-earn-points', InfluenceEarnPoints);
}

/*
 * ------------------------------
 * HISTORY
 * ------------------------------
*/
class InfluenceHistory extends InfluenceElement {
  constructor() {
    super();

    this.history = null;
  }

  async init(refresh = false) {
    this.history = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/customer/points?${
        new URLSearchParams({
          customer_id: this.initData.customerId,
          customer_email: this.initData.customerEmail,
          shop: this.initData.shop,
          digest: this.initData.customerDigest,
          limit: 99
        })
      }`, {}, refresh
    );

    this._renderHistory();
  }

  _renderHistory() {
    const historyTableWrapper = this.querySelector('.influence-user-data-history-table-wrapper');
    console.log('this.history =>', this.history)
    
    if (historyTableWrapper) {
      historyTableWrapper.innerHTML = '';
      const tableHeaders = [
        {
          tag: 'date',
          label: 'Date'
        },
        {
          tag: 'type',
          label: 'Type'
        },
        {
          tag: 'action',
          label: 'Action'
        },
        {
          tag: 'points',
          label: 'Points'
        }
      ];
      const historyTable = document.createElement('table');
      historyTable.classList.add('influence-user-data-history-table');

      const historyTableThead = document.createElement('thead');
      const historyTableTheadTr = document.createElement('tr');

      const historyTableTbody = document.createElement('tbody');

      tableHeaders.forEach(th => {
        const historyTableTheadTh = document.createElement('th');

        historyTableTheadTh.innerHTML = `<div>${this._t(th.label)}</div>`;
        historyTableTheadTr.appendChild(historyTableTheadTh);
      });

      this.history?.items?.forEach(h => {
        const historyTableTbodyTr = document.createElement('tr');

        tableHeaders.forEach(th => {
          const historyTableTbodyTd = document.createElement('td');

          switch (th.tag) {
            case "date":
              const date = new Date(h.createdAt);

              historyTableTbodyTd.innerHTML = `${date.toLocaleDateString()}`;
              break;

            case "type":
              historyTableTbodyTd.innerHTML = `${this._t(h.type)}`;
              break;

            case "action":
              historyTableTbodyTd.innerHTML = `${this._t(h.action)}`;
              break;

            case "points":
              historyTableTbodyTd.innerHTML = `${h.points}`;
              break;

            default:
              historyTableTbodyTd.innerHTML = `-`;
          }

          historyTableTbodyTr.appendChild(historyTableTbodyTd);
        })
        historyTableTbody.appendChild(historyTableTbodyTr);
      });

      historyTableThead.appendChild(historyTableTheadTr);

      historyTable.appendChild(historyTableThead);
      historyTable.appendChild(historyTableTbody);

      historyTableWrapper.appendChild(historyTable);
    }
  }
}

if (typeof window.customElements.get('influence-history') === 'undefined') {
  window.customElements.define('influence-history', InfluenceHistory);
}

/*
 * ------------------------------
 * REDEEM
 * ------------------------------
*/
class InfluenceRedeem extends InfluenceElement {
  constructor() {
    super();

    this.redeem = null;

    this.modal = this.querySelector('#influence_redeem_modal');
  }

  async init(refresh = false) {
    this.redeem = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/shop/rules/redeem?${
        new URLSearchParams({
          shop: this.initData.shop,
        })
      }`, {}, refresh
    );

    this._generateRedeem();
    
    MicroModal.init({
      openTrigger: 'data-custom-open',
      closeTrigger: 'data-custom-close',
      openClass: 'is-open',
      disableScroll: true,
      disableFocus: false,
      awaitOpenAnimation: true,
      awaitCloseAnimation: true,
      debugMode: false
    });


    if (!refresh) {
      const buttons = document.querySelectorAll('[data-custom-close="influence_redeem_modal"]');
  
      buttons.forEach(button => {
        button.addEventListener('click', () => {
          MicroModal.close('influence_redeem_modal')
        })
      });
    }
  }

  _updateTable() {
    const updateSelectors = [ 'influence-rewards', 'influence-history', 'influence-self-points' ];
    for (const selector of updateSelectors) {
      const elem = document.querySelector(selector);
      if (elem) {
        elem.refresh();
      }
    }
  }

  _generateRedeem() {
    const rules = this.redeem?.rules;
    const redeemElement = this.querySelector('.influence-user-data-redeem-items');

    if (redeemElement && rules && Array.isArray(rules)) {
      redeemElement.innerHTML = '';
      const sortedRules = rules.sort((a, b) => {
        if (a.discountValue > b.discountValue) {
          return 1;
        }
        if (a.discountValue < b.discountValue) {
          return -1;
        }
        return 0;
      });

      sortedRules.forEach(rule => {
        if (rule?.status == "Active") {
          const newDiv = document.createElement('div');
          newDiv.classList.add('influence-user-data-redeem-item');

          const iconElement = document.createElement('div');
          iconElement.classList.add('influence-user-data-redeem-item-icon');
          iconElement.innerHTML = this.settings.redeem_icons.redeem;

          const titleElement = document.createElement('div');
          titleElement.classList.add('influence-user-data-redeem-item-title');
          titleElement.innerHTML = `${this._t(rule.customTitle)}`;
          console.log('rule.customTitle => ', rule.customTitle)
          const costElement = document.createElement('div');
          costElement.classList.add('influence-user-data-redeem-item-cost');
          costElement.innerHTML = `${rule.pointCost} ${this._t('Punten')}`;

          const spendElement = document.createElement('div');
          spendElement.classList.add('influence-user-data-redeem-item-spend');
          spendElement.innerHTML = `${this._t('Minimale uitgave')} ${rule.minSpendAmount}€`;

          newDiv.appendChild(iconElement);
          newDiv.appendChild(titleElement);
          newDiv.appendChild(costElement);
          newDiv.appendChild(spendElement);

          if (this.initData?.customerId) {
            const buttonElement = document.createElement('button');
            buttonElement.className = 'button influence-user-data-redeem-item-button';
            buttonElement.innerText = this._t('Redeem');
            //buttonElement.setAttribute('onclick', `(this) => {redeem(this, '${rule?.id}');}`);
            buttonElement.addEventListener('click', (e) => {
              this.redeemPoints(e, rule?.id);
            });

            newDiv.appendChild(buttonElement);
          }

          redeemElement.appendChild(newDiv);
        }
      });
    }
  }

  redeemPoints(event, id) {
    const { target: button } =  event;

    button.classList.add('loading-button');

    const options = {
      method: 'POST',
      headers: {accept: 'application/json', 'content-type': 'application/json'},
      body: JSON.stringify({
        customer: {
          id: this.initData?.customerId,
          email: this.initData?.customerEmail
        },
        ruleId: id,
        redemptionSource: 'custom-rao-widget',
        shop: this.initData?.shop,
        digest: this.initData?.customerDigest,
      })
    };

    fetch('https://launcher.api.influence.io/launcher/v1/customer/redeem', options)
      .then(res => res.json())
      .then(res => {
        console.log(res);
        const modalMessageElement = this.querySelector('.influence-redeem-modal-message');

        if (res?.error) {
          if (res.error == 'NotEnoughPoints') {
            modalMessageElement.innerHTML = 'Not Enough Points';
            this.redeemModalOpen();
          }
        }

        if (res?.success) {
          modalMessageElement.innerHTML = `Coupon code: ${res.couponCode}`;
          this.redeemModalOpen();
          this._updateTable();
        }
        button.classList.remove('loading-button');
      })
      .catch(err => {
        console.error(err);
        button.classList.remove('loading-button');
      });
  }

  redeemModalOpen() {
    MicroModal.show('influence_redeem_modal');
  }
}

if (typeof window.customElements.get('influence-redeem') === 'undefined') {
  window.customElements.define('influence-redeem', InfluenceRedeem);
}

/*
 * ------------------------------
 * REWARDS
 * ------------------------------
*/
class InfluenceRewards extends InfluenceElement {
  constructor() {
    super();

    this.rewards = null;

    this.settings.no_rewards = this.getAttribute('data-no-rewards');
  }
  
  async init(refresh = false) {
    this.rewards = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/customer/all-rewards?${
        new URLSearchParams({
          customer_id: this.initData.customerId,
          customer_email: this.initData.customerEmail,
          shop: this.initData.shop,
          digest: this.initData.customerDigest,
          limit: 99
        })
      }`, {}, refresh
    );

    this._renderRewards();
  }

  _renderRewards() {
    const rewardsTableWrapper = this.querySelector('.influence-user-data-rewards-table-wrapper');

    if (rewardsTableWrapper) {
      rewardsTableWrapper.innerHTML = '';
      if (this.rewards?.items.length == 0) {
        const emptyTable = document.createElement('div');
        emptyTable.classList.add('influence-user-data-rewards-empty-table');
        emptyTable.innerHTML = this.settings.no_rewards;

        rewardsTableWrapper.appendChild(emptyTable);
      } else {
        const tableHeaders = [
          {
            tag: 'date',
            label: this.settings.date
          },
          {
            tag: 'reward',
            label: this.settings.reward
          },
          {
            tag: 'code',
            label: this.settings.code
          }
        ]
        const rewardsTable = document.createElement('table');
        rewardsTable.classList.add('influence-user-data-rewards-table');

        const rewardsTableThead = document.createElement('thead');
        const rewardsTableTheadTr = document.createElement('tr');

        const rewardsTableTbody = document.createElement('tbody');

        tableHeaders.forEach(th => {
          const rewardsTableTheadTh = document.createElement('th');

          rewardsTableTheadTh.innerHTML = `<div>${th.label}</div>`;
          rewardsTableTheadTr.appendChild(rewardsTableTheadTh);
        })

        this.rewards?.items?.forEach(r => {
          const rewardsTableTbodyTr = document.createElement('tr');

          tableHeaders.forEach(th => {
            const rewardsTableTbodyTd = document.createElement('td');

            switch (th.tag) {
              case "date":
                const date = new Date(r.createdAt);

                rewardsTableTbodyTd.innerHTML = `${date.toLocaleDateString()}`;
                break;

              case "reward":
                rewardsTableTbodyTd.innerHTML = `${this._t(r?.redeemRule?.title)}`;
                break;

              case "code":
                rewardsTableTbodyTd.innerHTML = `${this._t(r.couponCode)}`;
                break;

              case "status":
                rewardsTableTbodyTd.innerHTML = `${this._t(r?.redeemRule?.status)}`;
                break;

              default:
                rewardsTableTbodyTd.innerHTML = `-`;
            }

            rewardsTableTbodyTr.appendChild(rewardsTableTbodyTd);
          })
          rewardsTableTbody.appendChild(rewardsTableTbodyTr);
        })

        rewardsTableThead.appendChild(rewardsTableTheadTr);

        rewardsTable.appendChild(rewardsTableThead);
        rewardsTable.appendChild(rewardsTableTbody);

        rewardsTableWrapper.appendChild(rewardsTable);
      }
    }
  }
}

if (typeof window.customElements.get('influence-rewards') === 'undefined') {
  window.customElements.define('influence-rewards', InfluenceRewards);
}

/*
 * ------------------------------
 * SELF POINTS
 * ------------------------------
*/
class InfluenceSelfPoints extends InfluenceElement {
  constructor() {
    super();

    this.customer = null;
    this.loyaltyTiers = null;

    this.pointsElement = this.querySelector('.user-points');
    this.tierElement = this.querySelector('.user-tier');
    this.tierImageElement = this.querySelector('.user-tier-image');
  }
  
  async init(refresh = false) {
    this.customer = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/customer?${
        new URLSearchParams({
          customer_id: this.initData.customerId,
          customer_email: this.initData.customerEmail,
          shop: this.initData.shop,
          digest: this.initData.customerDigest,
        })
      }`, {}, refresh
    );

    this.loyaltyTiers = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/shop/tiers?${
        new URLSearchParams({
          shop: this.initData.shop,
        })
      }`, {}, refresh
    );

    if (this.customer) {
      const points = this.customer.pointBalance.toLocaleString();

      if (this.pointsElement) {
        this.pointsElement.innerHTML = points;
      }

      if (this.loyaltyTiers) {
        const loyaltyTier = this.loyaltyTiers.rules.find(el => el.id == this.customer.currentLoyaltyTierId);
        const tierData = Object.entries(this.settings.custom_tier_data).find(t => t[0].toLowerCase().trim() === loyaltyTier.title.toLowerCase().trim());
        
        if (this.tierElement) {
          this.tierElement.innerHTML = this._t(loyaltyTier.title);
        }

        if (this.tierImageElement && tierData) {
          this.tierImageElement.innerHTML = '';

          const imageUrl = tierData[1].image_url;

          const img = document.createElement('img');
          img.src = imageUrl;
          img.width = img.height = this.settings.image_width;

          this.tierImageElement.appendChild(img);
        }
      }
    }
  }
}

if (typeof window.customElements.get('influence-self-points') === 'undefined') {
  window.customElements.define('influence-self-points', InfluenceSelfPoints);
}

/*
 * ------------------------------
 * TIERS
 * ------------------------------
*/
class InfluenceTiers extends InfluenceElement {
  constructor() {
    super();

    this.loyaltyTiers = null;

    this.tiersContainer = this.querySelector('.influence-tiers-container');
  }

  async init(refresh = false) {
    this.loyaltyTiers = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/shop/tiers?${
        new URLSearchParams({
          shop: this.initData.shop,
        })
      }`, {}, refresh
    );

    if (this.loyaltyTiers) {
      this.tiersContainer.innerHTML = '';
      const sortedRules = this.loyaltyTiers.rules.sort((a, b) => a.threshold - b.threshold);
      for (const tier of sortedRules) {
        this._renderTier(tier);
      }
    }
  }

  _renderTier(tier) {
    const elem = document.createElement('div');
    elem.classList.add('influence-tier');

    const currentTierData = Object.entries(this.settings.custom_tier_data).find(t => t[0].toLowerCase().trim() === tier.title.toLowerCase().trim())[1] || {
      image_url: '',
      description: ''
    };

    const imgUrl = currentTierData.image_url;

    elem.appendChild(this._generateImage(imgUrl, this._t(tier.title)));
    elem.appendChild(this._generateSpan(this._t(tier.title), [ 'influence-tier--title', 'bold' ]));
    elem.appendChild(this._generateSpan(this._t(currentTierData.description), [ 'influence-tier--description' ]));
    elem.appendChild(this._generateBenefits(tier));

    this.tiersContainer.appendChild(elem);
  }
  
  _generateImage(url, alt) {
    const elem = document.createElement('img');
    elem.src = url;
    elem.alt = alt;
    elem.width = elem.height = this.settings.image_width;
    return elem;
  }

  _generateBenefits(tier) {
    const benefitsContainer = document.createElement('div');
    benefitsContainer.classList.add('influence-tier--benefits-container');
    benefitsContainer.appendChild(this._generateSpan(`${this._t(this.settings.benefits)}:`, [ 'bold' ]));

    const benefits = document.createElement('div');
    benefits.classList.add('influence-tier--benefits');
    for (const benefit of tier.perks) {
      const benefitElem = this._generateSpan(this._t(benefit), [ 'influence-tier--benefit' ]);
      benefitElem.insertAdjacentHTML('afterbegin', this.settings.tick_icon);
      benefits.appendChild(benefitElem);
    }
    benefitsContainer.appendChild(benefits);

    return benefitsContainer;
  }

  _generateSpan(text, classes = []) {
    const elem = document.createElement('span');
    for (const cls of classes) {
      elem.classList.add(cls);
    }
    elem.innerHTML = text;
    return elem;
  }
}

if (typeof window.customElements.get('influence-tiers') === 'undefined') {
  window.customElements.define('influence-tiers', InfluenceTiers);
}

/*
 * ------------------------------
 * YOUR BENEFITS
 * ------------------------------
*/
class InfluenceYourBenefits extends InfluenceElement {
  constructor() {
    super();

    this.customer = null;
    this.loyaltyTiers = null;
  }

  async init(refresh = false) {
    this.customer = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/customer?${
        new URLSearchParams({
          customer_id: this.initData.customerId,
          customer_email: this.initData.customerEmail,
          shop: this.initData.shop,
          digest: this.initData.customerDigest,
        })
      }`, {}, refresh
    );

    this.loyaltyTiers = await this._fetchWithRetry(
      `https://launcher.api.influence.io/launcher/v1/shop/tiers?${
        new URLSearchParams({
          shop: this.initData.shop,
        })
      }`, {}, refresh
    );

    const loyaltyTier = this.loyaltyTiers.rules.find(el => el.id == this.customer.currentLoyaltyTierId)
    const perks = loyaltyTier?.perks;
    const betefitsElement = this.querySelector('.influence-user-data-betefits-items');

    if (betefitsElement && perks && Array.isArray(perks)) {
      betefitsElement.innerHTML = '';
      perks.forEach(perk => {
        const newDiv = document.createElement('div');
        newDiv.classList.add('influence-user-data-betefits-item');

        newDiv.innerHTML = `<span>${this._t(perk)}</span>`;

        betefitsElement.appendChild(newDiv);
      })
    }
  }
}

if (typeof window.customElements.get('influence-your-benefits') === 'undefined') {
  window.customElements.define('influence-your-benefits', InfluenceYourBenefits);
}